import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageNavigator } from '../components/ui/PageNavigator';
import { Users, User, Target, Shield, Briefcase, Globe, Award, FileText, CheckCircle, Eye, Settings, ClipboardCheck, MapPin, Zap, BarChart, ArrowRight, Layers, Truck, Clock, Activity } from 'lucide-react';
import { VisualBreak } from '../components/ui/VisualBreak';
import { KPIStrip } from '../components/ui/KPIStrip';
import { PowerStatement } from '../components/ui/PowerStatement';

export const About = () => {
  const sections = [
    { id: 'company-snapshot', label: 'Company Snapshot' },
    { id: 'operational-philosophy', label: 'Operational Philosophy' },
    { id: 'why-energipro', label: 'Why EnergiPro' },
    { id: 'leadership', label: 'Governance & Accountability' },
    { id: 'technical-team', label: 'Key Personnel' },
    { id: 'operational-footprint', label: 'Operational Footprint' },
    { id: 'client-experience', label: 'Client & Project Experience' },
    { id: 'client-engagement', label: 'Client Engagement' },
    { id: 'operational-guarantees', label: 'Operational Guarantees' },
  ];

  return (
    <Layout>
      <SeoHead
        title="About EnergiPro Group | Execution Capability & Regional Presence"
        description="EnergiPro Group: 75+ projects delivered, 120+ equipment units, 85+ trained personnel. Operational across Saudi Arabia, UAE, and Egypt with 48-72hr mobilization."
        canonicalPath="/about"
      />
      {/* 1. HERO — Execution Capability & Technical Domains */}
      <div className="bg-slate-900 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-orange-400 text-sm font-bold uppercase tracking-widest mb-3">Surface Well Testing • Stimulation & Nitrogen • Field Execution Support</p>
          <div className="flex items-center gap-4 mb-6">
            <Briefcase className="text-orange-600" size={48} />
            <h1 className="text-4xl md:text-5xl font-bold text-white">EXECUTION CAPABILITY PROFILE</h1>
          </div>
          <div className="space-y-4 max-w-4xl">
            <p className="text-xl text-slate-300 leading-relaxed">
              EnergiPro Solutions deploys surface well testing equipment (10K & 1440 PSI), stimulation & nitrogen packages, filtration skids, and oilfield chemicals with trained field crews across Saudi Arabia, UAE & Egypt. We execute for operators and EPC contractors who require mobilization within 48–72 hours and direct management oversight on every operation.
            </p>
            <p className="text-lg text-slate-400 leading-relaxed">
              Technical domains: high-pressure well testing, nitrogen displacement & stimulation, produced water treatment, chemical injection programs, heavy transport & cross-border logistics. All equipment maintained to API 6A / 16C standards with traceable inspection records.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button href="/request-quote" variant="primary" className="px-8 py-3">
                SUBMIT PREQUALIFICATION INQUIRY
              </Button>
              <Button href="/files/energipro-capability-statement.pdf" variant="outline-light" className="px-8 py-3">
                DOWNLOAD CAPABILITY STATEMENT
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scale Indicators KPI Strip */}
      <KPIStrip
        background="accent"
        items={[
          { value: '75', suffix: '+', label: 'Projects Executed' },
          { value: '120', suffix: '+', label: 'Equipment Units' },
          { value: '85', suffix: '+', label: 'Personnel Deployed' },
          { value: '48–72', suffix: ' hrs', label: 'Mobilization Baseline' },
        ]}
      />

      <PageNavigator sections={sections} />

      {/* 2. COMPANY SNAPSHOT — With Scale Indicators */}
      <Section id="company-snapshot">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">COMPANY SNAPSHOT</h2>
          <p className="text-slate-600 max-w-3xl">
            Organizational and operational parameters for vendor evaluation and prequalification scoring.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Award className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Leadership Experience</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">20+</span>
            <span className="text-sm text-slate-600">Years in upstream pressure operations & field execution</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Activity className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Projects Executed</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">75+</span>
            <span className="text-sm text-slate-600">Well testing, stimulation & field support campaigns delivered</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Truck className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Equipment Fleet</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">120+</span>
            <span className="text-sm text-slate-600">Units across separators, pumps, tanks, flow iron & transport</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Users className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Operational Manpower</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">85+</span>
            <span className="text-sm text-slate-600">Field crews, supervisors, technicians & HSE personnel</span>
          </div>
        </div>

        {/* Additional scale data */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Globe className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Operational Regions</span>
            <span className="block text-xl font-bold text-slate-900 mb-1">KSA • UAE • Egypt</span>
            <span className="text-sm text-slate-600">Equipment yards & offices in all regions</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Layers className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Service Lines</span>
            <span className="block text-sm font-bold text-slate-900 mb-1">Well Testing • Stimulation • Chemicals • Filtration • Equipment • Logistics • Field Ops</span>
            <span className="text-sm text-slate-600">7 integrated lines, single commercial interface</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Settings className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Equipment Categories</span>
            <span className="block text-sm font-bold text-slate-900 mb-1">Separators • HP Pumps • Frac Tanks • Flow Iron • Cranes • Generators</span>
            <span className="text-sm text-slate-600">API 6A/16C certified, field-ready</span>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm shadow-sm">
            <div className="bg-orange-50 w-14 h-14 flex items-center justify-center rounded-full mb-4">
              <Clock className="text-orange-700" size={28} />
            </div>
            <span className="block text-sm text-slate-500 font-medium uppercase tracking-wide mb-1">Mobilization Baseline</span>
            <span className="block text-3xl font-bold text-slate-900 mb-1">48–72 hrs</span>
            <span className="text-sm text-slate-600">Equipment staged & crews on standby for rapid deployment</span>
          </div>
        </div>
      </Section>

      {/* Visual Break */}
      <VisualBreak
        image="/assets/showcase/batch-4/desert-wellsite-operations.png"
        statement="Direct management involvement. 2-hour escalation path. No bureaucratic delays."
        height="sm"
      />

      {/* 3. OPERATIONAL PHILOSOPHY — Measurable & Enforceable */}
      <Section id="operational-philosophy" background="light">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">OPERATIONAL PHILOSOPHY</h2>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Operations governed by documented procedures, defined control systems, and auditable outputs. No activity proceeds without approved scope, risk assessment, and assigned accountability.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="bg-orange-100 p-2 rounded-full mt-0.5 flex-shrink-0">
                  <Target className="text-orange-700" size={18} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Scope-Controlled Execution</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Every job executed against an approved Work Order with defined scope, equipment list, manning table, and acceptance criteria. Deviations require formal Management of Change (MOC-001 procedure) with Operations Manager sign-off before proceeding.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="bg-orange-100 p-2 rounded-full mt-0.5 flex-shrink-0">
                  <Shield className="text-orange-700" size={18} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Pre-Mobilization Risk Gate</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    HAZID/JSA completed and signed before every mobilization. High-risk activities (H₂S environments, night operations, simultaneous operations) require Level 2 supervisory approval plus HSE review per procedure EPI-HSE-004. Risk register updated within 24 hours of any new hazard identification.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="bg-orange-100 p-2 rounded-full mt-0.5 flex-shrink-0">
                  <BarChart className="text-orange-700" size={18} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Performance Measurement & Reporting</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    KPIs tracked: equipment uptime (target {'>'} 95%), NPT per campaign, first-time-right data acceptance rate, corrective action closure within 14 days. Monthly performance reports issued to Operations Director with trend analysis and action items.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="bg-orange-100 p-2 rounded-full mt-0.5 flex-shrink-0">
                  <FileText className="text-orange-700" size={18} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">Audit-Ready Documentation System</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Document control per EPI-QMS-002: inspection certificates, pressure test records, calibration logs, personnel competency records, and field reports maintained in controlled filing system. Retention period: 5 years minimum. Available for client audit within 48 hours of request.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="h-64 lg:h-80 overflow-hidden rounded-sm mb-6">
              <img src="/assets/field-oil-rig.jpg" alt="EnergiPro field operations" className="w-full h-full object-cover" />
            </div>
            <div className="bg-slate-900 text-white p-6 rounded-sm">
              <h4 className="font-bold mb-3 text-lg">Enforceable Controls</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-orange-500 flex-shrink-0" /> Stop Work Authority: Exercised 12 times in last 12 months — all documented</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-orange-500 flex-shrink-0" /> MOC requirement: Zero unauthorized deviations recorded</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-orange-500 flex-shrink-0" /> Pre-mob equipment inspection: 100% compliance verified per EPI-MNT-003</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-orange-500 flex-shrink-0" /> Lessons learned: Integrated into procedure updates within 30 days of event</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. WHY ENERGIPRO — Speed, Direct Management, Flexibility */}
      <Section id="why-energipro">
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">WHY ENERGIPRO</h2>
          <p className="text-slate-600 max-w-3xl">
            Differentiators relevant to procurement engineers evaluating vendor capability, responsiveness, and risk during prequalification and tender scoring.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:border-orange-300 transition-colors">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Zap className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Faster Than Large Contractors</h3>
            <p className="text-sm text-slate-600">
              48–72 hour mobilization from contract award to field arrival. No multi-layered approval chains, no regional HQ sign-off delays. Equipment pre-staged in KSA/UAE/Egypt yards with pre-mob inspections completed weekly.
            </p>
            <div className="mt-3 bg-slate-50 p-2 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">VS. LARGE CONTRACTORS: 7–14 day typical lead time</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:border-orange-300 transition-colors">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Eye className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Direct Management on Every Job</h3>
            <p className="text-sm text-slate-600">
              CEO and Operations Director personally accountable for field outcomes. No account managers as intermediaries. Client escalations reach decision-makers within hours, not days. Management conducts quarterly site visits on active projects.
            </p>
            <div className="mt-3 bg-slate-50 p-2 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">ESCALATION: Client → Ops Director within 2 hours</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:border-orange-300 transition-colors">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Settings className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Flexible Execution Models</h3>
            <p className="text-sm text-slate-600">
              Scope adjustments processed same-day. Contract models: call-out, day-rate, lump-sum, or hybrid. Equipment packages configurable per well program — not locked into standard fleet configurations.
            </p>
            <div className="mt-3 bg-slate-50 p-2 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">SCOPE CHANGE: Approved & executed within 24 hours</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:border-orange-300 transition-colors">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Layers className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Single-Source Accountability</h3>
            <p className="text-sm text-slate-600">
              Equipment, chemicals, crews, and logistics through one commercial entity. One PO, one HSE plan, one point of contact. Eliminates multi-vendor interface risk that causes NPT on complex well programs.
            </p>
            <div className="mt-3 bg-slate-50 p-2 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">RESULT: Zero interface-related NPT across 75+ projects</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:border-orange-300 transition-colors">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <FileText className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Audit-Ready from Day One</h3>
            <p className="text-sm text-slate-600">
              ISO 9001, ISO 14001, and ISO 45001 certified. Equipment certifications, personnel CVs, insurance certificates, HSE statistics, and IMS documentation pre-packaged for vendor evaluation. ISNetworld, Avetta & Veriforce compatible. No follow-up requests required for standard prequalification data.
            </p>
            <div className="mt-3 bg-slate-50 p-2 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">PREQUALIFICATION: Complete package delivered within 5 business days</span>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:border-orange-300 transition-colors">
            <div className="bg-orange-50 w-12 h-12 flex items-center justify-center rounded-full mb-4">
              <Globe className="text-orange-700" size={22} />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">Regional Knowledge, Local Execution</h3>
            <p className="text-sm text-slate-600">
              Permanent staff in all three operating countries. No fly-in crews for standard operations. Understanding of local regulatory requirements (MODON, SASO, Aramco SAPMT) embedded in operational planning.
            </p>
            <div className="mt-3 bg-slate-50 p-2 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">LOCAL COMPLIANCE: 100% regulatory adherence, zero violations</span>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. GOVERNANCE & ACCOUNTABILITY — With Performance Indicators */}
      <Section id="leadership" background="light">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Users className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">GOVERNANCE & ACCOUNTABILITY</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            Operational governance structure with defined accountabilities, audit frequency, and measurable compliance indicators. Led by professionals with 20+ years direct experience in well testing, pressure pumping, and field execution across GCC and MENA.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Eye size={20} className="text-orange-600" /> Executive Oversight
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> CEO accountable for TRIR and commercial performance; monthly board review</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Operations Director owns field execution outcomes and NPT metrics</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Direct management involvement in all incident investigations (24-hour response)</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Quarterly field visits by senior leadership to active project sites</li>
            </ul>
          </div>
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <ClipboardCheck size={20} className="text-orange-600" /> Authority Matrix & Escalation
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Defined delegation of authority: operational, financial, and HSE decisions</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Stop Work Authority: all personnel levels, exercised 12 times in last 12 months</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Escalation pathway: Field → Supervisor → Ops Manager → Director (max 4 hours)</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Authority matrix reviewed annually; last update: Q1 2026</li>
            </ul>
          </div>
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Shield size={20} className="text-orange-600" /> Field Supervision Structure
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Dedicated HSE supervisor at every active site (1:15 supervisor-to-crew ratio)</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Operations Supervisor accountable for execution quality and schedule adherence</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Daily progress reports submitted by 18:00; exception-based escalation triggers defined</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Crew competency verified before every mobilization; no waivers issued</li>
            </ul>
          </div>
          <div className="bg-white p-6 border border-slate-200 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart size={20} className="text-orange-600" /> Audit & Compliance Performance
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Internal IMS audits: quarterly (4x per year), all functions covered</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Corrective action closure rate: 94% within 14 days; 100% within 30 days</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Non-conformance tracking: 23 NCRs raised in 12 months, 22 closed, 1 in progress</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> Management review: bi-annual; last review April 2026 with 9 improvement actions assigned</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* 6. LEADERSHIP & KEY PERSONNEL */}
      <Section id="technical-team">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Users className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">LEADERSHIP TEAM</h2>
          </div>
          <p className="text-slate-600 max-w-4xl">
            Named leadership with direct field experience in well testing, pressure operations, and asset integrity across GCC and MENA energy projects. Every senior leader holds current industry certifications and maintains active involvement in project execution.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* CEO */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-48 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
              <User className="text-slate-600" size={64} />
            </div>
            <div className="p-6">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Chief Executive Officer</span>
              <h3 className="font-bold text-slate-900 text-lg mt-1">Raed Ashmawy</h3>
              <p className="text-sm text-slate-500 mt-1 mb-3">20+ years in upstream oilfield services</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Former Managing Director at Energiprogroup with direct oversight of well testing, pressure control, and field engineering operations across Saudi Arabia, UAE, and Egypt. Personally accountable for TRIR and commercial performance. Conducts quarterly site visits on active projects.
              </p>
              <div className="mt-4">
                <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Focus Areas</span>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">Strategic Direction</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">Client Relations</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">HSE Accountability</span>
                </div>
              </div>
            </div>
          </div>

          {/* Operations Director */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-48 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
              <User className="text-slate-600" size={64} />
            </div>
            <div className="p-6">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Operations Director</span>
              <h3 className="font-bold text-slate-900 text-lg mt-1">Mohamed Amin</h3>
              <p className="text-sm text-slate-500 mt-1 mb-3">18+ years in field operations & equipment management</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Owns field execution outcomes and NPT metrics. Responsible for end-to-end project execution from mobilization through demobilization. Direct involvement in all incident investigations within 24 hours.
              </p>
              <div className="mt-4">
                <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Focus Areas</span>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">Field Execution</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">NPT Reduction</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">Equipment Integrity</span>
                </div>
              </div>
            </div>
          </div>

          {/* HSE Manager */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-48 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
              <User className="text-slate-600" size={64} />
            </div>
            <div className="p-6">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">HSE Manager</span>
              <h3 className="font-bold text-slate-900 text-lg mt-1">Ahmed Kamal</h3>
              <p className="text-sm text-slate-500 mt-1 mb-3">15+ years in health, safety & environment</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Field execution authority for all HSE matters. Manages IMS implementation, audit programs, and competency validation. Oversees 100% JSA compliance and stop-work authorization across all operations.
              </p>
              <div className="mt-4">
                <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Focus Areas</span>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">IMS Implementation</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">Audit Programs</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">Competency Validation</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-400 mt-8 italic">
          Full leadership bios with photographs available in the vendor prequalification package. Contact us to request the complete leadership profile.
        </p>
      </Section>

      {/* Power Statement */}
      <PowerStatement text="Structured operations. Audit-ready documentation. Zero ambiguity in execution." variant="dark" />

      {/* 7. OPERATIONAL FOOTPRINT — With Equipment, Mobilization & Capabilities */}
      <Section id="operational-footprint" background="light">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">OPERATIONAL FOOTPRINT</h2>
          </div>
          <p className="text-slate-600 max-w-3xl">
            Equipment availability, mobilization timelines, and operational capabilities by region.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* KSA */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-slate-900 w-12 h-12 flex items-center justify-center rounded-full">
                <span className="text-white font-bold text-sm">KSA</span>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Saudi Arabia — Primary Operations Base</h3>
                <span className="text-xs text-slate-500">Al Khobar, Eastern Province</span>
              </div>
            </div>
            <div className="space-y-3 text-sm text-slate-600">
              <div>
                <span className="font-semibold text-slate-800">Equipment Available:</span>
                <p>Separators (10K/1440 PSI), choke manifolds, HP flow iron, frac tanks, nitrogen units, HP pumps, cranes (25–120T), generators, chemical injection skids</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Mobilization:</span>
                <p>48 hours to Eastern Province sites; 72 hours to Central/Western regions</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Capabilities:</span>
                <p>Full well testing, stimulation support, chemical supply, heavy transport, fabrication & recertification</p>
              </div>
            </div>
          </div>

          {/* UAE */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-slate-900 w-12 h-12 flex items-center justify-center rounded-full">
                <span className="text-white font-bold text-sm">UAE</span>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">United Arab Emirates — Equipment Staging & Support</h3>
                <span className="text-xs text-slate-500">Dubai (KIMKO, EPTS)</span>
              </div>
            </div>
            <div className="space-y-3 text-sm text-slate-600">
              <div>
                <span className="font-semibold text-slate-800">Equipment Available:</span>
                <p>Flow iron packages, test separators, chemical inventory, support vehicles, portable generators</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Mobilization:</span>
                <p>48 hours to Abu Dhabi onshore/offshore staging; 72 hours for cross-border to Oman</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Capabilities:</span>
                <p>Equipment staging, well testing support, chemical supply, ADNOC-aligned operations</p>
              </div>
            </div>
          </div>

          {/* Egypt */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-slate-900 w-12 h-12 flex items-center justify-center rounded-full">
                <span className="text-white font-bold text-sm">EGY</span>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Egypt — Field Support & Chemical Supply</h3>
                <span className="text-xs text-slate-500">Western Desert / Gulf of Suez</span>
              </div>
            </div>
            <div className="space-y-3 text-sm text-slate-600">
              <div>
                <span className="font-semibold text-slate-800">Equipment Available:</span>
                <p>Chemical inventory, filtration skids, support equipment, light vehicles, portable testing units</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Mobilization:</span>
                <p>72 hours to Western Desert; 48 hours to Gulf of Suez from Cairo staging</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Capabilities:</span>
                <p>Chemical programs, filtration services, field support crews, logistics coordination</p>
              </div>
            </div>
          </div>

          {/* MENA Expansion */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-slate-900 w-12 h-12 flex items-center justify-center rounded-full">
                <span className="text-white font-bold text-sm">MENA</span>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">MENA Region — Project-Specific Deployment</h3>
                <span className="text-xs text-slate-500">North Africa</span>
              </div>
            </div>
            <div className="space-y-3 text-sm text-slate-600">
              <div>
                <span className="font-semibold text-slate-800">Equipment Available:</span>
                <p>Deployed from KSA/UAE yards based on project requirements and contract scope</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Mobilization:</span>
                <p>7–14 days including customs clearance and cross-border permits</p>
              </div>
              <div>
                <span className="font-semibold text-slate-800">Capabilities:</span>
                <p>Full service delivery with dedicated project teams; requires advance planning and import coordination</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 9. NEW SECTION — Client & Project Experience Summary */}
      <Section id="client-experience">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">CLIENT & PROJECT EXPERIENCE SUMMARY</h2>
          </div>
          <p className="text-slate-600 max-w-3xl">
            Types of clients served and project categories delivered, demonstrating relevant experience for operator and EPC contractor evaluation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Client Types */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 text-lg border-b border-slate-200 pb-3">Client Categories</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">National Oil Companies (NOCs)</span>
                  <p className="text-sm text-slate-600">Direct support campaigns for major national operators in KSA, UAE & Egypt</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">International Operators (IOCs)</span>
                  <p className="text-sm text-slate-600">Equipment & field services for international E&P companies operating in MENA</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Tier-1 EPC Contractors</span>
                  <p className="text-sm text-slate-600">Subcontracted equipment, crews, and logistics for large-scale development projects</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Oilfield Service Companies</span>
                  <p className="text-sm text-slate-600">Equipment rental and operational support for service companies requiring fleet augmentation</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Drilling Contractors</span>
                  <p className="text-sm text-slate-600">Wellsite support equipment, chemicals, and logistics for active drilling campaigns</p>
                </div>
              </div>
            </div>
          </div>

          {/* Project Types */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm">
            <h3 className="font-bold text-slate-900 mb-4 text-lg border-b border-slate-200 pb-3">Project Categories Delivered</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Surface Well Testing Campaigns</span>
                  <p className="text-sm text-slate-600">Multi-well programs (3–12 wells), exploration & appraisal testing, extended well tests</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Stimulation & Nitrogen Operations</span>
                  <p className="text-sm text-slate-600">HP nitrogen displacement, acid stimulation support, coiled tubing auxiliary services</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Flowline & Piping Fabrication</span>
                  <p className="text-sm text-slate-600">HP/LP piping spools, manifold assemblies, pressure-tested per ASME B31.3 with NDT</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Chemical Treatment Programs</span>
                  <p className="text-sm text-slate-600">Corrosion inhibition, scale treatment, H₂S scavenging, production optimization chemicals</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Equipment Recertification & Integrity</span>
                  <p className="text-sm text-slate-600">API 6A body & hydro recertification, NDT campaigns, valve overhauls, pressure vessel inspection</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-slate-900 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-slate-800">Logistics & Heavy Transport</span>
                  <p className="text-sm text-slate-600">Heavy-haul to remote wellsites, cross-border mobilization, customs clearance, last-mile delivery</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-900 text-white p-4 rounded-sm text-center">
            <span className="block text-2xl font-bold text-orange-400">75+</span>
            <span className="text-xs text-slate-400">Projects Delivered</span>
          </div>
          <div className="bg-slate-900 text-white p-4 rounded-sm text-center">
            <span className="block text-2xl font-bold text-orange-400">185K+</span>
            <span className="text-xs text-slate-400">Man-Hours LTI-Free</span>
          </div>
          <div className="bg-slate-900 text-white p-4 rounded-sm text-center">
            <span className="block text-2xl font-bold text-orange-400">0.00</span>
            <span className="text-xs text-slate-400">TRIR (12-Month Rolling)</span>
          </div>
          <div className="bg-slate-900 text-white p-4 rounded-sm text-center">
            <span className="block text-2xl font-bold text-orange-400">3</span>
            <span className="text-xs text-slate-400">Countries Active</span>
          </div>
        </div>
      </Section>

      {/* CLIENT ENGAGEMENT MODEL */}
      <Section id="client-engagement" background="light">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">CLIENT ENGAGEMENT MODELS</h2>
          </div>
          <p className="text-slate-600 max-w-3xl">
            Flexible commercial structures supporting operator and EPC project requirements across varying timelines, scopes, and operational complexities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 border border-slate-200 rounded-sm border-t-4 border-t-orange-600">
            <h3 className="font-bold text-slate-900 mb-3 text-lg">Rental & Call-Out Services</h3>
            <p className="text-sm text-slate-600 mb-4">
              Equipment rental with optional crew deployment for short-term needs. Pre-inspected and certified equipment available on call-out basis with 48-hour response.
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Well testing packages (10K/1440 PSI)</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Flow iron, manifolds & HP connections</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Pump units, tanks & ancillary equipment</li>
            </ul>
          </div>
          <div className="bg-white p-8 border border-slate-200 rounded-sm border-t-4 border-t-slate-900">
            <h3 className="font-bold text-slate-900 mb-3 text-lg">Project-Based Execution</h3>
            <p className="text-sm text-slate-600 mb-4">
              Full-scope delivery from planning through demobilization. Defined milestones, deliverables, and performance metrics agreed at contract award.
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Multi-well testing campaigns</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Fabrication & installation projects</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Equipment recertification programs</li>
            </ul>
          </div>
          <div className="bg-white p-8 border border-slate-200 rounded-sm border-t-4 border-t-slate-900">
            <h3 className="font-bold text-slate-900 mb-3 text-lg">Long-Term Operational Support</h3>
            <p className="text-sm text-slate-600 mb-4">
              Ongoing contracts for sustained operations including resident crews, preventive maintenance, logistics, and chemical supply on retainer or rate-based models.
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Resident crew deployment (field/yard)</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Preventive maintenance programs</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-orange-600" /> Logistics & supply chain management</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* 8. OPERATIONAL GUARANTEES — Enforceable & Measurable */}
      <Section id="operational-guarantees">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Award className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">OPERATIONAL GUARANTEES</h2>
          </div>
          <p className="text-slate-600 max-w-3xl">
            Enforceable commitments with defined metrics, tracking mechanisms, and accountability structures. Not aspirations — observable and auditable behaviors.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 border-t-4 border-orange-600">
            <h3 className="font-bold text-slate-900 mb-3 text-lg">Execution Integrity</h3>
            <p className="text-slate-600 mb-4">
              All field data recorded accurately. Reports, test charts, and incident records maintained without omission or modification. Discrepancies reported within 4 hours.
            </p>
            <div className="bg-slate-50 p-3 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">MEASURED BY:</span>
              <p className="text-xs text-slate-600 mt-1">Client data acceptance rate: 98%+ first submission. Zero data integrity NCRs in 12 months. All field reports issued within 24 hours of test completion.</p>
            </div>
          </Card>
          <Card className="p-6 border-t-4 border-slate-900">
            <h3 className="font-bold text-slate-900 mb-3 text-lg">Procedural Discipline</h3>
            <p className="text-slate-600 mb-4">
              Work executed per approved plans, permits, and JSAs. No unauthorized modifications. Every deviation processed through formal MOC with documented justification.
            </p>
            <div className="bg-slate-50 p-3 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">MEASURED BY:</span>
              <p className="text-xs text-slate-600 mt-1">100% JSA compliance before task execution. Zero procedural violations in 12 months. MOC response time: under 4 hours for critical, 24 hours for non-critical.</p>
            </div>
          </Card>
          <Card className="p-6 border-t-4 border-slate-900">
            <h3 className="font-bold text-slate-900 mb-3 text-lg">Competency Assurance</h3>
            <p className="text-slate-600 mb-4">
              No personnel deployed with expired certifications, incomplete training, or unverified competency. Role-specific assessment completed before every mobilization.
            </p>
            <div className="bg-slate-50 p-3 rounded-sm">
              <span className="text-xs text-slate-500 font-medium">MEASURED BY:</span>
              <p className="text-xs text-slate-600 mt-1">100% certification currency at deployment. Zero competency-related incidents. Re-certification tracked 90 days before expiry with automated alerts.</p>
            </div>
          </Card>
        </div>
      </Section>

      {/* FINAL CTA — Request Capability Statement & Vendor Package */}
      <Section background="dark">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">REQUEST VENDOR PREQUALIFICATION PACKAGE</h2>
            <p className="text-slate-300 text-lg">
              Complete documentation set for operator and EPC vendor evaluation, tendering, and prequalification processes. Delivered within 5 business days.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <Briefcase className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">Company Profile & Financials</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <Settings className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">Equipment List & Certifications</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <Shield className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">HSE Statistics & IMS Docs</span>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center">
              <ClipboardCheck className="text-orange-500 mx-auto mb-2" size={24} />
              <span className="text-white text-sm font-medium block">Personnel CVs & Project Refs</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/files/energipro-capability-statement.pdf" variant="primary" className="px-8 py-4 text-lg">
              DOWNLOAD CAPABILITY STATEMENT
            </Button>
            <Button href="/request-quote" variant="outline-light" className="px-8 py-4 text-lg">
              REQUEST FULL VENDOR PACKAGE
            </Button>
          </div>
        </div>
      </Section>
    </Layout>
  );
};
